// Special Collection tool: attach a curated child-collection grid (image + title)
// to a parent Shopify collection via metafield special_collection.config.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import store from 'store2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faArrowDown,
	faArrowLeft,
	faArrowUp,
	faPlus,
	faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { ajaxPerso, confirmation_modal, fetchShopifyBrands, handelize, meta } from '../fnc'

const refConst = require("../constants.js").v

const META_NAMESPACE = "special_collection"
const META_KEY = "config"
const MAX_ITEMS = 30

const styleList = {
	container: (provided) => ({
		...provided,
		width: "100%",
	}),
	menu: (provided) => ({
		...provided,
		zIndex: 99999
	}),
}

const emptyItem = () => ({
	id: "",
	handle: "",
	title: "",
	image: "",
	_key: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
})

/**
 * Parse metafield JSON whether Shopify returned a string or already-parsed object.
 */
function parseConfigValue(raw) {
	if (!raw) return { title: "", items: [] }
	if (typeof raw === "object") {
		return {
			title: raw.title || "",
			items: Array.isArray(raw.items) ? raw.items : [],
		}
	}
	try {
		const parsed = JSON.parse(raw)
		return {
			title: parsed?.title || "",
			items: Array.isArray(parsed?.items) ? parsed.items : [],
		}
	} catch (e) {
		return { title: "", items: [] }
	}
}

export default function SpecialCollection(props) {
	if (!props.securityClearance(props)) return null

	const stores = refConst.stores.filter(o => o.id_marketplace == 14)
	const preSelected = store("storeSelectedSpecialCollection")
	const [shopSelected, setShopSelected] = useState(preSelected || stores[0] || null)
	const [collections, setCollections] = useState([])
	const [brandHandles, setBrandHandles] = useState([])
	const [indexList, setIndexList] = useState([])
	const [viewMode, setViewMode] = useState("list") // "list" | "editor"
	const [mainCollection, setMainCollection] = useState(null)
	const [sectionTitle, setSectionTitle] = useState("")
	const [items, setItems] = useState([])
	const [metaId, setMetaId] = useState(null)
	const [dirty, setDirty] = useState(false)
	const [filePickerItemKey, setFilePickerItemKey] = useState(null)
	const [fileSearch, setFileSearch] = useState("")
	const [searchResults, setSearchResults] = useState({ imgs: [], info: null })
	const [searchLoading, setSearchLoading] = useState(false)
	const uploadRefs = useRef({})

	const resetEditorState = () => {
		setMainCollection(null)
		setSectionTitle("")
		setItems([])
		setMetaId(null)
		setDirty(false)
		setFilePickerItemKey(null)
		setFileSearch("")
		setSearchResults({ imgs: [], info: null })
	}

	useEffect(() => {
		if (!shopSelected?.id) return
		resetEditorState()
		setViewMode("list")
		setIndexList([])
		loadCollections()
		loadIndex()
		loadBrandHandles()
	}, [shopSelected])

	useEffect(() => {
		if (viewMode !== "editor" || !shopSelected?.id || !mainCollection) {
			if (viewMode !== "editor") {
				setSectionTitle("")
				setItems([])
				setMetaId(null)
				setDirty(false)
			}
			return
		}
		loadExistingConfig()
	}, [shopSelected, mainCollection, viewMode])

	const collectionOptions = useMemo(() => {
		return collections.map(o => ({
			value: o.id,
			label: o.title,
			collection: o,
		}))
	}, [collections])

	const brandHandleSet = useMemo(() => new Set(brandHandles), [brandHandles])
	const isBrandPage = (collection) => {
		const collectionTitle = typeof collection === "string" ? collection : collection?.title
		return Boolean(collectionTitle && brandHandleSet.has(handelize(collectionTitle)))
	}

	const loadIndex = (done) => {
		if (!shopSelected?.id) return
		ajaxPerso({
			api: "shopify",
			trigger: "list_special_collections",
			shop: shopSelected.id,
		}, (r) => {
			if (r.errors) {
				props.info({ error: r.errors })
				setIndexList([])
			} else if (r.success) {
				setIndexList(Array.isArray(r.data?.collections) ? r.data.collections : [])
			} else {
				setIndexList([])
			}
			if (typeof done === "function") done()
		})
	}

	const loadCollections = () => {
		props.loading(true)
		ajaxPerso({
			api: "shopify",
			trigger: "downloadCollection",
			shop: shopSelected.id,
		}, (r) => {
			props.loading(false)
			if (!r.success) {
				props.info({ error: r.error || "Unable to load collections." })
				setCollections([])
				return
			}

			const sorted = (r.data || [])
				.map(o => ({
					id: String(o.id || o.legacyResourceId || ""),
					handle: o.handle || "",
					title: o.title || o.handle || "",
					defaultImage: o.image?.url || null,
				}))
				.filter(o => o.id && o.handle)
				.sort((a, b) => a.title.localeCompare(b.title))

			setCollections(sorted)
		})
	}

	const loadBrandHandles = () => {
		fetchShopifyBrands(shopSelected.id, null, null, (r) => {
			if (!r?.success || !Array.isArray(r.data)) {
				setBrandHandles([])
				return
			}

			setBrandHandles(r.data
				.map(brand => brand?.vendor || brand?.name || brand)
				.filter(brand => typeof brand === "string" && brand.trim())
				.map(handelize))
		})
	}

	const openFromIndex = (entry) => {
		const id = String(entry.id || "")
		const found = collections.find(c => c.id === id)
		resetEditorState()
		setViewMode("editor")
		if (found) {
			setMainCollection(found)
			return
		}
		// Collection missing from live download — still open with index metadata so Clear works.
		setMainCollection({
			id,
			handle: entry.handle || "",
			title: entry.title || entry.section_title || entry.handle || id,
			defaultImage: null,
		})
	}

	const startCreate = () => {
		resetEditorState()
		setViewMode("editor")
	}

	const closeEditor = (force = false) => {
		const doClose = () => {
			resetEditorState()
			setViewMode("list")
			loadIndex()
		}

		if (!force && dirty) {
			confirmation_modal(
				props,
				() => doClose(),
				null,
				<div className="mt-2">You have unsaved changes. Close the editor and discard them?</div>
			)
			return
		}
		doClose()
	}

	const loadExistingConfig = () => {
		props.loading(true)
		meta.fetch({
			shop: shopSelected.id,
			namespace: META_NAMESPACE,
			key: META_KEY,
			urlPrefix: `collections/${mainCollection.id}/`,
		}, (r) => {
			props.loading(false)
			if (r.success && r.res && r.res.length > 0) {
				const row = r.res[0]
				setMetaId(row.id)
				const parsed = parseConfigValue(row.value)
				setSectionTitle(parsed.title || mainCollection.title || "")
				setItems((parsed.items || []).map((item, idx) => ({
					id: String(item.id || ""),
					handle: item.handle || "",
					title: item.title || "",
					image: item.image || "",
					_key: `loaded_${idx}_${item.handle || idx}`,
				})))
			} else {
				setMetaId(null)
				setSectionTitle(mainCollection.title || "")
				setItems([])
			}
			setDirty(false)
		})
	}

	const markDirty = () => setDirty(true)

	const updateItem = (key, patch) => {
		setItems(prev => prev.map(item => item._key === key ? { ...item, ...patch } : item))
		markDirty()
	}

	const addItem = () => {
		if (items.length >= MAX_ITEMS) {
			props.info({ error: `Maximum of ${MAX_ITEMS} collections per Special Collection.` })
			return
		}
		setItems(prev => [...prev, emptyItem()])
		markDirty()
	}

	const removeItem = (key) => {
		setItems(prev => prev.filter(item => item._key !== key))
		markDirty()
	}

	const moveItem = (index, direction) => {
		const target = index + direction
		if (target < 0 || target >= items.length) return
		setItems(prev => {
			const next = [...prev]
			const tmp = next[index]
			next[index] = next[target]
			next[target] = tmp
			return next
		})
		markDirty()
	}

	const onPickChildCollection = (key, selectedOption) => {
		const col = selectedOption?.collection
		if (!col) {
			updateItem(key, { id: "", handle: "", title: "" })
			return
		}
		const current = items.find(i => i._key === key)
		updateItem(key, {
			id: col.id,
			handle: col.handle,
			// Keep a custom title if the user already typed one for a different collection;
			// otherwise default to the Shopify collection title.
			title: current?.title && current.handle === col.handle ? current.title : col.title,
			image: current?.image || col.defaultImage || "",
		})
	}

	const fetchAdminFiles = (args, callback) => {
		ajaxPerso(Object.assign({
			api: "shopify",
			trigger: "get_admin_files",
			shop: shopSelected.id,
			autoLoad: false,
			qty: 20,
			includeDetails: false,
		}, args), callback)
	}

	const searchExistingFiles = (afterCursor = null) => {
		const filterName = fileSearch.trim()
		if (!filterName) {
			props.info({ error: "Enter a filename search term." })
			return
		}

		setSearchLoading(true)
		if (!afterCursor) props.loading(true)

		const reqData = { filter_name: filterName }
		if (afterCursor) reqData.after = afterCursor

		fetchAdminFiles(reqData, (r) => {
			setSearchLoading(false)
			if (!afterCursor) props.loading(false)

			if (r.errors) {
				props.info({ error: r.errors })
				return
			}
			if (!r.success) {
				props.info({ error: "Unable to search Shopify files." })
				return
			}

			if (afterCursor) {
				setSearchResults(prev => ({
					imgs: prev.imgs.concat(r.urlList || []),
					info: r.pageInfo || null,
				}))
			} else {
				setSearchResults({
					imgs: r.urlList || [],
					info: r.pageInfo || null,
				})
			}
		})
	}

	const applyExistingFile = (fileObj) => {
		if (!filePickerItemKey || !fileObj?.url) return
		updateItem(filePickerItemKey, { image: fileObj.url })
		setFilePickerItemKey(null)
		setSearchResults({ imgs: [], info: null })
		setFileSearch("")
	}

	const uploadItemImage = (item) => {
		const input = uploadRefs.current[item._key]
		if (!item.handle) {
			props.info({ error: "Select a collection before uploading an image." })
			return
		}
		if (!input || !input.files || input.files.length !== 1) {
			props.info({ error: "Choose one image file to upload." })
			return
		}

		props.loading(true)
		const formData = new FormData()
		formData.append("img", input.files[0], input.files[0].name)
		formData.append("api", "shopify")
		formData.append("trigger", "upload_special_collection_image")
		formData.append("uid", props.getUser().uid)
		formData.append("shop", shopSelected.id)
		formData.append("collectionHandle", item.handle)
		formData.append("token", refConst.tokenEio)

		ajaxPerso(formData, (r) => {
			props.loading(false)
			if (r.errors) {
				props.info({ error: r.errors })
			} else if (r.success && r.data?.imageUrl) {
				updateItem(item._key, { image: r.data.imageUrl })
				props.info({ success: "Image uploaded to Shopify Files." })
				input.value = ""
			} else {
				props.info({ error: "Upload failed." })
			}
		}, null, {
			contentType: false,
			processData: false,
			cache: false,
			async: true,
		})
	}

	const buildPayload = () => {
		const cleaned = items
			.filter(item => item.handle && item.title)
			.map(item => ({
				id: String(item.id || ""),
				handle: item.handle,
				title: item.title.trim(),
				image: (item.image || "").trim(),
			}))

		return {
			title: (sectionTitle || mainCollection?.title || "").trim(),
			items: cleaned,
		}
	}

	const saveConfig = () => {
		if (!mainCollection) {
			props.info({ error: "Select the main collection first." })
			return
		}

		const payload = buildPayload()
		if (!payload.items.length) {
			props.info({ error: "Add at least one child collection before saving." })
			return
		}

		const missingImage = payload.items.filter(i => !i.image)
		if (missingImage.length) {
			props.info({
				error: `These collections need an image: ${missingImage.map(i => i.title).join(", ")}`,
			})
			return
		}

		props.loading(true)
		ajaxPerso({
			api: "shopify",
			trigger: "set_special_collection",
			uid: props.getUser().uid,
			shop: shopSelected.id,
			mainCollectionId: mainCollection.id,
			mainCollectionHandle: mainCollection.handle,
			mainCollectionTitle: mainCollection.title,
			config: JSON.stringify(payload),
			token: refConst.tokenEio,
		}, (r) => {
			props.loading(false)
			if (r.errors) {
				props.info({ error: r.errors })
			} else if (r.success) {
				if (r.data?.metafieldId) setMetaId(r.data.metafieldId)
				if (Array.isArray(r.data?.index?.collections)) {
					setIndexList(r.data.index.collections)
				} else {
					loadIndex()
				}
				setDirty(false)
				props.info({
					success: `Saved Special Collection on "${mainCollection.title}" (${payload.items.length} items).`,
				})
			} else {
				props.info({ error: "Save failed." })
			}
		})
	}

	const clearConfig = () => {
		if (!mainCollection) return

		confirmation_modal(
			props,
			() => {
				props.loading(true)
				ajaxPerso({
					api: "shopify",
					trigger: "set_special_collection",
					uid: props.getUser().uid,
					shop: shopSelected.id,
					mainCollectionId: mainCollection.id,
					mainCollectionHandle: mainCollection.handle,
					mainCollectionTitle: mainCollection.title,
					clear: true,
					token: refConst.tokenEio,
				}, (r) => {
					props.loading(false)
					if (r.errors) {
						props.info({ error: r.errors })
					} else if (r.success) {
						setMetaId(null)
						setItems([])
						setSectionTitle(mainCollection.title || "")
						setDirty(false)
						if (Array.isArray(r.data?.index?.collections)) {
							setIndexList(r.data.index.collections)
						} else {
							loadIndex()
						}
						props.info({ success: "Special Collection metafield removed." })
						resetEditorState()
						setViewMode("list")
					} else {
						props.info({ error: "Could not clear the metafield." })
					}
				})
			},
			null,
			<div className="mt-2">Remove the Special Collection grid from &quot;{mainCollection.title}&quot;?</div>
		)
	}

	const previewTitle = sectionTitle || mainCollection?.title || "Special Collection"

	return (
		<div className="main ctnPage container-fluid">
			<h1 className="page-header mb-2">Special Collection</h1>
			<p className="text-muted mb-4 text-wrap">
				Build a featured collection grid (image + title cards) and save it on a parent collection
				metafield for the theme:{" "}
				<code>collection.metafields.special_collection.config</code>.
				A shop-level registry is kept in sync at{" "}
				<code>shop.metafields.special_collection.index</code>.
			</p>

			<div className="p-3 bg-light border mb-3">
				<p className="font-weight-bolder mb-2">Store</p>
				<Select
					className="w-50"
					styles={styleList}
					placeholder="Select store"
					isSearchable={true}
					isDisabled={viewMode === "editor"}
					options={stores.map(s => ({
						value: JSON.stringify(s),
						label: s.name,
					}))}
					value={shopSelected ? [{
						value: JSON.stringify(shopSelected),
						label: shopSelected.name,
					}] : null}
					onChange={(selectedOption) => {
						const next = selectedOption ? JSON.parse(selectedOption.value) : null
						store("storeSelectedSpecialCollection", next)
						setShopSelected(next)
					}}
				/>
			</div>

			{viewMode === "list" ? (
			<div className="p-3 bg-white border mb-3">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<div>
						<p className="font-weight-bolder mb-0">Saved Special Collections</p>
						<div className="small text-muted">
							All parent collections that currently have a Special Collection grid on this store.
						</div>
					</div>
					<div>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary mr-2"
							onClick={() => {
								props.loading(true)
								loadIndex(() => props.loading(false))
							}}
						>
							Refresh list
						</button>
						<button
							type="button"
							className="btn btn-sm btn-primary"
							onClick={startCreate}
						>
							<FontAwesomeIcon icon={faPlus} className="mr-1" />
							Create new
						</button>
					</div>
				</div>
				{indexList.length === 0 ? (
					<div className="alert alert-secondary mb-0 py-2 small">
						No Special Collections saved yet. Click <strong>Create new</strong> to add one.
					</div>
				) : (
					<div className="table-responsive">
						<table className="table table-sm table-hover mb-0">
							<thead>
								<tr>
									<th>Collection</th>
									<th>Section title</th>
									<th className="text-center">Items</th>
									<th>Updated</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{indexList.map((entry) => (
									<tr key={entry.id}>
										<td>
											<div className="font-weight-bold">
												{entry.title || entry.handle || entry.id}
												{isBrandPage(entry) ? <span className="badge badge-info ml-2">Brand page</span> : null}
											</div>
											<div className="small text-muted">
												<code>/collections/{entry.handle || "?"}</code>
											</div>
										</td>
										<td>{entry.section_title || "—"}</td>
										<td className="text-center">{entry.item_count ?? 0}</td>
										<td className="small text-muted">
											{entry.updated_at
												? new Date(entry.updated_at).toLocaleString()
												: "—"}
										</td>
										<td className="text-right">
											<button
												type="button"
												className="btn btn-sm btn-outline-primary"
												onClick={() => openFromIndex(entry)}
											>
												Edit
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
			) : (
			<>
			<div className="d-flex justify-content-between align-items-center mb-3 p-3 border rounded bg-white">
				<div>
					<button
						type="button"
						className="btn btn-outline-secondary"
						onClick={() => closeEditor(false)}
					>
						<FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
						Back to list
					</button>
					{dirty ? <span className="badge badge-warning ml-2">Unsaved changes</span> : null}
				</div>
				<div className="font-weight-bold">
					{mainCollection
						? `Editing: ${mainCollection.title}`
						: "New Special Collection"}
				</div>
			</div>

			<div className="p-3 bg-light border mb-3">
				<p className="font-weight-bolder mb-2">Main collection</p>
				<p className="small text-muted mb-2">
					The collection page that will show this grid (metafield is saved on this collection).
				</p>
				<Select
					styles={styleList}
					placeholder="Select main collection..."
					isSearchable={true}
					options={collectionOptions}
					value={mainCollection ? [{
						value: mainCollection.id,
						label: mainCollection.title,
					}] : null}
					onChange={(selectedOption) => {
						setMainCollection(selectedOption ? selectedOption.collection : null)
						setDirty(true)
					}}
				/>
				{mainCollection ? (
					<div className="small text-muted mt-2">
						Handle: <code>{mainCollection.handle}</code>
						{metaId ? <span className="ml-3 badge badge-success">Config saved</span> : null}
						{dirty ? <span className="ml-2 badge badge-warning">Unsaved changes</span> : null}
					</div>
				) : null}
			</div>

			{mainCollection ? (
				<>
					<div className="p-3 bg-light border mb-3">
						<label className="font-weight-bolder mb-2 d-block" htmlFor="specialCollectionTitle">
							Section title
						</label>
						<input
							id="specialCollectionTitle"
							type="text"
							className="form-control w-50"
							placeholder={mainCollection.title}
							value={sectionTitle}
							onChange={(e) => {
								setSectionTitle(e.target.value)
								markDirty()
							}}
						/>
						<div className="small text-muted mt-1">
							Shown as the heading above the grid (defaults to the main collection title).
						</div>
					</div>

					<div className="d-flex justify-content-between align-items-center mb-2">
						<h5 className="mb-0">
							Child collections{" "}
							<span className="text-muted small">({items.length}/{MAX_ITEMS})</span>
						</h5>
						<button type="button" className="btn btn-primary btn-sm" onClick={addItem}>
							<FontAwesomeIcon icon={faPlus} className="mr-1" />
							Add collection
						</button>
					</div>

					{items.length === 0 ? (
						<div className="alert alert-secondary">
							No child collections yet. Add collections to build the grid (e.g. Socket Sets, Pliers Sets…).
						</div>
					) : (
						items.map((item, index) => {
							const selectedChild = item.id
								? collectionOptions.find(o => o.value === item.id)
								: null

							return (
								<div key={item._key} className="border rounded bg-white p-3 mb-3">
									<div className="d-flex justify-content-between align-items-start mb-3">
										<div className="font-weight-bold">#{index + 1}</div>
										<div className="btn-group">
											<button
												type="button"
												className="btn btn-outline-secondary btn-sm"
												disabled={index === 0}
												onClick={() => moveItem(index, -1)}
												title="Move up"
											>
												<FontAwesomeIcon icon={faArrowUp} />
											</button>
											<button
												type="button"
												className="btn btn-outline-secondary btn-sm"
												disabled={index === items.length - 1}
												onClick={() => moveItem(index, 1)}
												title="Move down"
											>
												<FontAwesomeIcon icon={faArrowDown} />
											</button>
											<button
												type="button"
												className="btn btn-outline-danger btn-sm"
												onClick={() => removeItem(item._key)}
												title="Remove"
											>
												<FontAwesomeIcon icon={faTrash} />
											</button>
										</div>
									</div>

									<div className="row">
										<div className="col-md-5 mb-3">
											<label className="small font-weight-bold text-uppercase text-muted d-block">
												Collection
											</label>
											<Select
												styles={styleList}
												placeholder="Select collection..."
												isSearchable={true}
												options={collectionOptions.filter(o =>
													o.value === item.id || o.value !== mainCollection.id
												)}
												value={selectedChild ? [{
													value: selectedChild.value,
													label: selectedChild.label,
												}] : null}
												onChange={(opt) => onPickChildCollection(item._key, opt)}
											/>
											{item.handle ? (
												<div className="small text-muted mt-1">
													<code>/collections/{item.handle}</code>
												</div>
											) : null}
										</div>
										<div className="col-md-4 mb-3">
											<label className="small font-weight-bold text-uppercase text-muted d-block">
												Card title
											</label>
											<input
												type="text"
												className="form-control"
												value={item.title}
												placeholder="e.g. Socket & Ratchet Sets"
												onChange={(e) => updateItem(item._key, { title: e.target.value })}
											/>
										</div>
										<div className="col-md-3 mb-3">
											<label className="small font-weight-bold text-uppercase text-muted d-block">
												Preview
											</label>
											{item.image ? (
												<div
													style={{
														height: "90px",
														borderRadius: "10px",
														overflow: "hidden",
														border: "1px solid #ddd",
														background: `transparent url('${item.image}') center / contain no-repeat`,
													}}
													title={item.title || "Preview"}
												/>
											) : (
												<div
													style={{
														height: "90px",
														borderRadius: "10px",
														overflow: "hidden",
														background: "#2b2b2b",
														display: "flex",
														border: "1px solid #ddd",
													}}
												>
													<div
														style={{
															flex: "0 0 48%",
															background: "#e8e8e8",
															clipPath: "polygon(0 0, 100% 0, 82% 100%, 0 100%)",
														}}
													/>
													<div
														style={{
															flex: 1,
															color: "#fff",
															fontSize: "11px",
															fontWeight: 700,
															display: "flex",
															alignItems: "center",
															padding: "0 8px",
															lineHeight: 1.2,
														}}
													>
														{item.title || "Title"}
													</div>
												</div>
											)}
										</div>
									</div>

									<div className="row align-items-end">
										<div className="col-md-6 mb-2">
											<label className="small font-weight-bold text-uppercase text-muted d-block">
												Upload image
											</label>
											<div className="d-flex">
												<input
													ref={(el) => { uploadRefs.current[item._key] = el }}
													type="file"
													accept="image/*"
													className="form-control-file"
													disabled={!item.handle}
												/>
												<button
													type="button"
													className="btn btn-sm btn-success ml-2 text-nowrap"
													disabled={!item.handle}
													onClick={() => uploadItemImage(item)}
												>
													Upload
												</button>
											</div>
											<div className="small text-muted mt-1">
												Stored as <code>specialCol_{item.handle || "handle"}</code> in Shopify Files.
											</div>
										</div>
										<div className="col-md-6 mb-2">
											<label className="small font-weight-bold text-uppercase text-muted d-block">
												Or use existing file / URL
											</label>
											<div className="d-flex">
												<input
													type="text"
													className="form-control"
													placeholder="https://cdn.shopify.com/..."
													value={item.image}
													onChange={(e) => updateItem(item._key, { image: e.target.value })}
												/>
												<button
													type="button"
													className="btn btn-sm btn-outline-primary ml-2 text-nowrap"
													onClick={() => {
														setFilePickerItemKey(item._key)
														setFileSearch(item.handle ? `specialCol_${item.handle}` : "")
														setSearchResults({ imgs: [], info: null })
													}}
												>
													Browse Files
												</button>
											</div>
										</div>
									</div>
								</div>
							)
						})
					)}

					{filePickerItemKey ? (
						<div className="border rounded p-3 mb-3 bg-light">
							<div className="d-flex justify-content-between align-items-center mb-2">
								<strong>Pick a Shopify Files image</strong>
								<button
									type="button"
									className="btn btn-sm btn-link"
									onClick={() => setFilePickerItemKey(null)}
								>
									Close
								</button>
							</div>
							<div className="d-flex mb-3">
								<input
									type="text"
									className="form-control"
									placeholder="Filename search, e.g. specialCol_"
									value={fileSearch}
									onChange={(e) => setFileSearch(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault()
											searchExistingFiles()
										}
									}}
								/>
								<button
									type="button"
									className="btn btn-primary ml-2"
									disabled={searchLoading || !fileSearch.trim()}
									onClick={() => searchExistingFiles()}
								>
									{searchLoading ? "Searching..." : "Search"}
								</button>
							</div>
							{searchResults.imgs.length > 0 ? (
								<div className="d-flex flex-wrap">
									{searchResults.imgs.map(o => (
										<div
											key={o.id}
											className="p-2"
											style={{ cursor: "pointer" }}
											onClick={() => applyExistingFile(o)}
											title={o.fileName}
										>
											<div
												style={{
													width: "100px",
													height: "100px",
													border: "1px solid #ddd",
													borderRadius: "4px",
													background: `#fff url('${o.url}') center / contain no-repeat`,
												}}
											/>
											<div className="small text-truncate" style={{ maxWidth: "100px" }}>
												{o.fileName}
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="small text-muted">Search Shopify Files to pick an existing image.</div>
							)}
							{searchResults.info?.hasNextPage ? (
								<button
									type="button"
									className="btn btn-link btn-sm"
									disabled={searchLoading}
									onClick={() => searchExistingFiles(searchResults.info.endCursor)}
								>
									Load more
								</button>
							) : null}
						</div>
					) : null}

					{items.length > 0 ? (
						<div
							className="border rounded p-4 mb-4"
							style={{
								background: "var(--eio-bg-elevated)",
								color: "var(--eio-text)",
								borderColor: "var(--eio-border)",
							}}
						>
							<div className="text-center font-weight-bold mb-3" style={{ fontSize: "1.4rem" }}>
								{previewTitle}
							</div>
							<div
								style={{
									display: "grid",
									gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
									gap: "14px",
								}}
							>
								{items.filter(i => i.title || i.image).map(item => (
									item.image ? (
										<div
											key={`preview_${item._key}`}
											title={item.title || "Untitled"}
											style={{
												height: "110px",
												borderRadius: "14px",
												overflow: "hidden",
												background: `transparent url('${item.image}') center / contain no-repeat`,
											}}
										/>
									) : (
										<div
											key={`preview_${item._key}`}
											style={{
												height: "110px",
												borderRadius: "14px",
												overflow: "hidden",
												background: "#2b2b2b",
												display: "flex",
											}}
										>
											<div
												style={{
													flex: "0 0 48%",
													background: "#dcdcdc",
													clipPath: "polygon(0 0, 100% 0, 82% 100%, 0 100%)",
												}}
											/>
											<div
												style={{
													flex: 1,
													color: "#fff",
													fontWeight: 700,
													display: "flex",
													alignItems: "center",
													padding: "0 12px",
													fontSize: "14px",
													lineHeight: 1.25,
												}}
											>
												{item.title || "Untitled"}
											</div>
										</div>
									)
								))}
							</div>
						</div>
					) : null}

					<div className="d-flex align-items-center mb-5">
						<button
							type="button"
							className="btn btn-success px-4"
							onClick={saveConfig}
							disabled={props.loading() || items.length === 0}
						>
							Save Special Collection
						</button>
						<button
							type="button"
							className="btn btn-outline-danger ml-2"
							onClick={clearConfig}
							disabled={props.loading() || !metaId}
						>
							Delete metafield
						</button>
						<button
							type="button"
							className="btn btn-outline-secondary ml-2"
							onClick={() => closeEditor(false)}
						>
							Close
						</button>
						<div className="small text-muted ml-3">
							Theme section should read{" "}
							<code>collection.metafields.special_collection.config.value</code>
						</div>
					</div>
				</>
			) : (
				<div className="alert alert-info">
					Select a main collection above to start building the grid.
				</div>
			)}
			</>
			)}
		</div>
	)
}
